import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Input, Form, message } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

const FaqManager = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);

  const [editRecord, setEditRecord] = useState(null);

  const [formAdd] = Form.useForm();
  const [formEdit] = Form.useForm();

  // Fetch FAQs
  const fetchFaqs = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/faqs");
      setFaqs(res.data.faqs || []);
    } catch (err) {
      message.error("Failed to load FAQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  // Add FAQ
  const submitAdd = async () => {
    try {
      const values = await formAdd.validateFields();
      await axiosInstance.post("/faqs", values);
      message.success("FAQ added");
      formAdd.resetFields();
      setOpenAddModal(false);
      fetchFaqs();
    } catch (err) {
      message.error("Failed to add FAQ");
    }
  };

  // Edit FAQ
  const openEdit = (record) => {
    setEditRecord(record);
    formEdit.setFieldsValue({
      question: record.question,
      answer: record.answer,
    });
    setOpenEditModal(true);
  };

  const submitEdit = async () => {
    try {
      const values = await formEdit.validateFields();
      await axiosInstance.put(`/faqs/${editRecord.id}`, values);
      message.success("FAQ updated");
      setOpenEditModal(false);
      fetchFaqs();
    } catch (err) {
      message.error("Failed to update FAQ");
    }
  };

  // Delete FAQ
  const deleteFaq = (id) => {
    Modal.confirm({
      title: "Delete FAQ?",
      icon: <DeleteOutlined />,
      okType: "danger",
      onOk: async () => {
        try {
          await axiosInstance.delete(`/faqs/${id}`);
          message.success("FAQ deleted");
          fetchFaqs();
        } catch (err) {
          message.error("Failed to delete FAQ");
        }
      },
    });
  };

  // Table Columns
  const columns = [
    {
      title: "Question",
      dataIndex: "question",
      render: (q) => <strong className="text-gray-800">{q}</strong>,
    },
    {
      title: "Answer",
      dataIndex: "answer",
      render: (a) => <span className="text-gray-600">{a}</span>,
    },
    {
      title: "Actions",
      width: 200,
      render: (_, record) => (
        <div className="flex gap-2">
          <Button
            icon={<EditOutlined />}
            className="border-blue-500 text-blue-600"
            onClick={() => openEdit(record)}
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteFaq(record.id)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
      <div className="max-w-6xl mx-auto">

        {/* Header Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-2">
                <QuestionCircleOutlined /> Manage FAQs
              </h1>
              <p className="text-gray-600">Create and manage all frequently asked questions</p>
            </div>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setOpenAddModal(true)}
              className="bg-blue-600 hover:bg-blue-700 border-blue-600 h-10 px-4"
            >
              Add FAQ
            </Button>
          </div>
        </div>

        {/* Table Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <Table
            dataSource={faqs}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 6 }}
          />
        </div>
      </div>

      {/* ADD FAQ MODAL */}
      <Modal
        title="Add FAQ"
        open={openAddModal}
        footer={null}
        onCancel={() => setOpenAddModal(false)}
      >
        <Form layout="vertical" form={formAdd} onFinish={submitAdd}>
          <Form.Item
            name="question"
            label="Question"
            rules={[{ required: true, message: "Enter the question" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="answer"
            label="Answer"
            rules={[{ required: true, message: "Enter the answer" }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          <div className="flex justify-end gap-2">
            <Button onClick={() => setOpenAddModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Add
            </Button>
          </div>
        </Form>
      </Modal>

      {/* EDIT FAQ MODAL */}
      <Modal
        title="Edit FAQ"
        open={openEditModal}
        footer={null}
        onCancel={() => setOpenEditModal(false)}
      >
        <Form layout="vertical" form={formEdit} onFinish={submitEdit}>
          <Form.Item
            name="question"
            label="Question"
            rules={[{ required: true, message: "Enter the question" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="answer"
            label="Answer"
            rules={[{ required: true, message: "Enter the answer" }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          <div className="flex justify-end gap-2">
            <Button onClick={() => setOpenEditModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Update
            </Button>
          </div>
        </Form>
      </Modal>

    </div>
  );
};

export default FaqManager;
